/**
 * An item's file that's a document or spreadsheet (lib/documents.ts), shown by Revisa's own viewer instead of a static
 * preview: read-only, its colours as printed or for a dark screen ("Dark colors"), with Edit for those who write. An
 * Office file's is in Beta (said under it, with its original a click away, and its own preview one toggle away); a
 * file the server can't give as JSON falls back to its static preview. An imported word-processing document with a
 * PDF made of it reads as that PDF instead (DocumentPdf), our viewer being for editing it. The viewer and the editor
 * load as a chunk of their own (office/documentsChunk.ts).
 */

import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { type ApiError, api } from "../../api/client.ts";
import type { DocKind, DocumentOut, ItemDetail, SpaceOut } from "../../api/types.gen.ts";
import { locale, t } from "../../i18n/index.ts";
import {
  betaLabel,
  docTypeOf,
  downloadBundle,
  downloadExport,
  type LostPart,
  lostParts,
  openEditor,
  showDark,
} from "../../lib/documents.ts";
import { useEditNudge, useEditTries } from "../../lib/editNudge.ts";
import { ignore } from "../../lib/ignore.ts";
import type { Shown } from "../../lib/itemFiles.ts";
import { holdGestures } from "../../lib/native.ts";
import { outsideOf, spaceApi, spaceUrl } from "../../lib/paths.ts";
import { onPhone } from "../../lib/platform.ts";
import { asApiError, useAction } from "../../lib/resource.ts";
import { navigate } from "../../lib/router.ts";
import { readFor, toast } from "../../lib/toast.ts";
import { type DocumentsModule, documentsChunk } from "../../office/documentsChunk.ts";
import type { NotesPane } from "../../office/review/notes.ts";
import { Button, InlineButton } from "../../ui/Button.tsx";
import { DarkToggle } from "../../ui/DarkToggle.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { OriginalIcon, SavedVersionIcon, SaveVersionIcon } from "../../ui/icons.tsx";
import { Menu, type MenuItem } from "../../ui/Menu.tsx";
import { PdfViewer } from "../../ui/pdf/PdfViewer.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { buttonClass, iconButtonClass, LINK } from "../../ui/styles.ts";
import { Suggestions } from "../comments/suggestions.tsx";

/** The documents chunk, once it's loaded (kept: switching between viewing and editing doesn't wait for it again). */
let chunk: DocumentsModule | null = null;

/** The documents chunk, once loaded. */
function useChunk(): DocumentsModule | null {
  const [mod, setMod] = useState<DocumentsModule | null>(chunk);
  useEffect(() => {
    if (!chunk) {
      documentsChunk.load().then((m) => {
        chunk = m;
        setMod(m);
      }, ignore);
    }
  }, []);
  return mod;
}

interface Loaded {
  doc?: DocumentOut;
  error?: ApiError;
}

/**
 * Documents fetched lately, by item and file: the editor and the viewer of one draw at once from it, no spinner. For a
 * few minutes (its pictures' links are signed for a while).
 */
const fetched = new Map<string, { doc: DocumentOut; at: number }>();
const FETCHED_MAX = 8;
const FETCHED_MS = 5 * 60_000;

function cachedDoc(key: string): DocumentOut | undefined {
  const hit = fetched.get(key);
  return hit && Date.now() - hit.at < FETCHED_MS ? hit.doc : undefined;
}

/**
 * Item `id`'s file as Revisa's JSON, fetched again only when its file changes (`sha256`): not on every live update,
 * which would redraw the viewer for nothing.
 */
function useDocument(space: SpaceOut, id: string, sha256: string): Loaded | null {
  const key = `${id}:${sha256}`;
  const cached = cachedDoc(key);
  const [got, setGot] = useState<{ key: string; loaded: Loaded } | null>(
    cached ? { key, loaded: { doc: cached } } : null,
  );
  // By its path: the space comes again as a new object with every live update. From outside (a public link's page, a
  // guest's), its page's own reads: of its item alone.
  const out = outsideOf(space);
  const path = out ? `${out.api}/document` : spaceApi(space, `/items/${id}/document`);
  useEffect(() => {
    const kept = cachedDoc(key);
    if (kept) {
      setGot({ key, loaded: { doc: kept } });
      return;
    }
    const ctl = new AbortController();
    api.get<DocumentOut>(path, undefined, ctl.signal).then(
      (doc) => {
        fetched.set(key, { doc, at: Date.now() });
        if (fetched.size > FETCHED_MAX) {
          fetched.delete(fetched.keys().next().value as string);
        }
        setGot({ key, loaded: { doc } });
      },
      (e: unknown) => {
        if (!ctl.signal.aborted) {
          setGot({ key, loaded: { error: asApiError(e) } });
        }
      },
    );
    return () => ctl.abort();
  }, [path, key]);
  // The same item's file saved again (live, as others edit): the one before shown meanwhile, not a spinner.
  return got && (got.key === key || (got.key.startsWith(`${id}:`) && got.loaded.doc)) ? got.loaded : null;
}

/**
 * "New document" and "New spreadsheet", where files are added (an item's attachments, the triage area): each makes one there
 * and opens it in its editor.
 */
export function NewDocuments({
  space,
  attachTo,
  triage = false,
  mine = false,
}: {
  space: SpaceOut;
  attachTo?: string;
  triage?: boolean;
  /** Private to you (your private triage area). */
  mine?: boolean;
}) {
  const [make, busy] = useAction(async (kind: DocKind) => {
    // A document in the language you write in (yours: its spell checking and numbers).
    // On a phone, a document's in free flow (no pages: as wide as its screen).
    const body = { kind, attach_to: attachTo ?? null, triage, private: mine, lang: locale.value, flow: onPhone() };
    const item = await api.post<ItemDetail>(spaceApi(space, "/files/new"), body);
    navigate(spaceUrl(space, `/items/${item.id}?edit=1`));
  });
  return (
    <span class="flex flex-wrap gap-x-4 gap-y-1">
      <InlineButton size="xs" disabled={busy} onClick={() => make("doc")}>
        {t("documents.newDoc")}
      </InlineButton>
      <InlineButton size="xs" disabled={busy} onClick={() => make("sheet")}>
        {t("documents.newSheet")}
      </InlineButton>
    </span>
  );
}

/**
 * "Download", a menu: a document or spreadsheet as Word or Excel, or as a PDF; one of Revisa's as its bundle, an
 * Office file as its original (`original`: its link; none, not offered) and, of another format (`.xls`, `.odt`), as
 * the Office one.
 */
export function DocumentDownload({
  space,
  id,
  kind,
  native,
  original,
}: {
  space: SpaceOut;
  id: string;
  kind: DocKind;
  native: boolean;
  /** An Office file's original: its link and extension (`.xlsx`); whether it's already the Office format. */
  original?: { url: string; ext: string; office: boolean } | undefined;
}) {
  const [busy, setBusy] = useState(false);
  // What the last export left out, listed when its toast's Details is pressed.
  const [report, setReport] = useState<{ format: string; parts: string[] } | null>(null);
  const run = (go: () => Promise<LostPart[] | undefined>, format?: string) => {
    setBusy(true);
    go()
      .then((lost) => {
        if (lost?.length && format) {
          const text = t("documents.export.leftOut", { format });
          toast(text, "info", readFor(text), [
            { label: t("documents.export.details"), onClick: () => setReport({ format, parts: lostParts(lost) }) },
          ]);
        }
      })
      .catch((e: unknown) => toast(asApiError(e).message, "error"))
      .finally(() => setBusy(false));
  };
  const office = kind === "doc" ? ("docx" as const) : ("xlsx" as const);
  const items: MenuItem[] = [];
  if (native) {
    items.push({
      key: "bundle",
      label: t(kind === "doc" ? "documents.export.bundleDoc" : "documents.export.bundleSheet"),
      onSelect: () =>
        run(async () => {
          await downloadBundle(space, id);
          return [];
        }),
    });
  } else if (original) {
    items.push({
      key: "original",
      label: t("documents.export.original", { ext: original.ext }),
      onSelect: () => window.open(original.url, "_blank", "noopener,noreferrer"),
    });
  }
  if (native || !original?.office) {
    items.push({
      key: office,
      label: t(kind === "doc" ? "documents.export.docx" : "documents.export.xlsx"),
      onSelect: () => run(() => downloadExport(space, id, office), kind === "doc" ? "Word" : "Excel"),
    });
  }
  items.push({
    key: "pdf",
    label: t("documents.export.pdf"),
    onSelect: () => run(() => downloadExport(space, id, "pdf"), "PDF"),
  });
  return (
    <span class={`inline-flex items-center gap-1.5 ${busy ? "opacity-60" : ""}`} data-download="">
      <Menu label={t("documents.download")} items={items} class={LINK} />
      {/* Exporting can take seconds (a PDF most): it's under way. */}
      {busy ? <Spinner small={true} /> : null}
      <Dialog
        open={report !== null}
        onClose={() => setReport(null)}
        title={t("documents.export.reportTitle", { format: report?.format ?? "" })}
      >
        <p class="text-sm text-zinc-600">{t("documents.export.reportIntro")}</p>
        <ul class="flex list-disc flex-col gap-1.5 pl-5 text-sm text-zinc-900" data-export-report="">
          {report?.parts.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <DialogActions>
          <Button answer="confirm" variant="primary" onClick={() => setReport(null)}>
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </span>
  );
}

/** The original as it came (its static preview) instead of Revisa's viewer: an icon in the viewer's bar, pressed when on. */
function OriginalToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      class={iconButtonClass("xs")}
      aria-pressed={on}
      aria-label={t("documents.showOriginal")}
      title={t("documents.showOriginal")}
      onClick={onToggle}
    >
      <OriginalIcon />
    </button>
  );
}

/**
 * `shown` (a document or spreadsheet) in Revisa's viewer, or `fallback` (its static preview) when the server can't
 * give it as JSON, or when asked to show the original (an imported file's: the same button, pressed, above it). `onEdit`: those who may edit it get Edit.
 */
export function DocumentPreview({
  space,
  shown,
  fill = false,
  onEdit,
  fallback,
  notesPane,
  showNotes,
}: {
  space: SpaceOut;
  shown: Shown;
  fill?: boolean;
  onEdit?: (() => void) | undefined;
  fallback: ComponentChildren;
  /** Hands a document's review pane to the page (comments and findings in place: notesSlot.tsx). */
  notesPane?: ((p: NotesPane) => ComponentChildren) | undefined;
  /** Shows the page's side panel, where the pane is. */
  showNotes?: (() => void) | undefined;
}) {
  const type = docTypeOf(shown.file.mime);
  const loaded = useDocument(space, shown.id, shown.file.sha256);
  const mod = useChunk();
  const [original, setOriginal] = useState(false);
  const nudged = useEditNudge();
  const tries = useEditTries();
  if (!type || loaded?.error || original) {
    return (
      <>
        {original ? (
          <div class="flex justify-end">
            <OriginalToggle on={true} onToggle={() => setOriginal(false)} />
          </div>
        ) : null}
        {fallback}
      </>
    );
  }
  const doc = loaded?.doc;
  if (!(doc && mod)) {
    return <Spinner />;
  }
  const beta = betaLabel(type, false);
  // Its original as it came, where there's something to see (an image of it, its text).
  const hasOriginal = beta !== null && (shown.preview !== undefined || Boolean(shown.text));
  const controls = (
    // Edit first, then what's about viewing it (as the editor has Done before them).
    <>
      {onEdit ? (
        <span ref={nudged} class="mr-1 inline-flex rounded-md">
          <Button size="sm" onClick={onEdit}>
            {t("documents.edit")}
          </Button>
        </span>
      ) : null}
      <DarkToggle />
      {hasOriginal ? <OriginalToggle on={false} onToggle={() => setOriginal(true)} /> : null}
    </>
  );
  return (
    // Trying to change it points out Edit (lib/editNudge.ts).
    <div ref={tries} class={`flex min-h-0 flex-col gap-2 ${fill ? "flex-1" : ""}`} data-viewer={doc.kind}>
      <mod.DocumentView
        space={space}
        doc={doc}
        item={shown.id}
        version={shown.file.sha256}
        notesPane={notesPane}
        showNotes={showNotes}
        locale={locale.value}
        dark={showDark()}
        fill={fill}
        controls={controls}
      />
    </div>
  );
}

/**
 * An open editor's own actions (`openEditor`): Save version ("Saved", blue, when nothing's changed since the last save;
 * a click still makes it a version) and Done. Where Edit was: the item page's tab bar, a quick look's editor bar.
 */
export function EditingActions() {
  const ed = openEditor.value;
  return ed ? (
    <>
      {/* "Save version", grey with a plus, while there are changes since the last save; "Saved", blue with a check, when
            there are none (a click still makes it a version). */}
      <button
        type="button"
        class={`${buttonClass("ghost", "sm")} ${ed.saved ? "text-blue-600! hover:text-blue-700!" : "text-zinc-600"}`}
        title={ed.saved ? ed.status || t("documents.allSaved") : t("office.save")}
        onClick={ed.saveVersion}
      >
        {ed.saved ? <SavedVersionIcon /> : <SaveVersionIcon />}
        {ed.saved ? t("documents.savedShort") : t("office.save")}
      </button>
      <Button variant="primary" size="sm" busy={ed.leaving} onClick={ed.done}>
        {t("documents.done")}
      </Button>
    </>
  ) : null;
}

/**
 * An imported word-processing document read as the PDF LibreOffice made of it (`pdf`): the PDF viewer, its text
 * selectable, laid out as Office lays it out (the same metric-compatible fonts). Edit (for those who write) opens
 * Revisa's editor on its converted JSON, in Beta there; reading it isn't. `fill`: as tall as its box (a quick look's),
 * where the PDF's toolbar links to the original.
 */
export function DocumentPdf({
  shown,
  pdf,
  fill = false,
  onEdit,
}: {
  shown: Shown;
  pdf: string;
  fill?: boolean;
  onEdit?: (() => void) | undefined;
}) {
  return (
    <div class={`flex min-h-0 flex-col gap-2 ${fill ? "flex-1" : ""}`} data-viewer="pdf">
      <PdfViewer
        url={pdf}
        title={shown.title}
        fill={fill}
        original={fill ? (shown.url ?? false) : false}
        controls={
          onEdit ? (
            <Button size="xs" class="ml-1" onClick={onEdit}>
              {t("documents.edit")}
            </Button>
          ) : null
        }
      />
    </div>
  );
}

/** `shown` (the item's own document or spreadsheet) being edited, until Done (`onDone`). */
export function DocumentEditing({
  space,
  shown,
  onDone,
  fill = false,
  actions = false,
  notesPane,
  showNotes,
}: {
  space: SpaceOut;
  shown: Shown;
  onDone: () => void;
  /** As tall as its box (the item's page gives it the window's room). */
  fill?: boolean;
  /** Its Save version and Done in its own bar (a quick look: no tab bar to have them). */
  actions?: boolean;
  /** Hands its review pane to the page (comments and findings in place: notesSlot.tsx). */
  notesPane?: ((p: NotesPane) => ComponentChildren) | undefined;
  /** Shows the page's side panel, where the pane is. */
  showNotes?: (() => void) | undefined;
}) {
  const type = docTypeOf(shown.file.mime);
  // Fetched once as the editor opens: the editor's copy is its own from then on (its saves change the item's file).
  const [opened] = useState(() => shown.file.sha256);
  const loaded = useDocument(space, shown.id, opened);
  const mod = useChunk();
  useEffect(() => {
    if (loaded?.error) {
      toast(loaded.error.message, "error");
      onDone();
    }
  }, [loaded, onDone]);
  // While it's open, its drags are its own: not the app's back swipe, nor its sheet's (a native app's); the page not
  // scrolled past its top (the window's room it fills, or itself).
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => holdGestures(() => root.current?.closest<HTMLElement>("[data-fill]") ?? root.current), []);
  const doc = loaded?.doc;
  if (!(type && doc && mod)) {
    return <Spinner />;
  }
  return (
    <div ref={root} class={`flex min-h-0 flex-col gap-2 ${fill ? "flex-1" : ""}`}>
      <mod.DocumentEditor
        suggestions={Suggestions}
        space={space}
        item={shown.id}
        title={shown.title}
        doc={doc}
        version={shown.file.sha256}
        notesPane={notesPane}
        showNotes={showNotes}
        locale={locale.value}
        dark={showDark()}
        onDone={onDone}
        controls={
          <>
            {actions ? <EditingActions /> : null}
            <DarkToggle />
          </>
        }
      />
    </div>
  );
}
